import {DestinationLocale} from '#lit/common/locale/destination.js';
import {html} from 'lit';
import type {Model, Msg} from './architecture.js';
import type {ActionButtonOption} from './types.js';
import {audienceExportLang} from '#locale/translations/audiences/exports.js';
import {locale} from '#locale';
import type {UpdateHandler} from '#lit/components/mixins/immutable_component/index.js';

const lang = audienceExportLang[locale.getLocale()];

export const ActionButtonKeys = {
  MANAGE: 'manage',
  EXTEND: 'extend',
  RUN: 'run',
  ENABLE: 'enable',
  DISABLE: 'disable',
  AUTHENTICATE: 'authenticate',
  INGEST: 'ingest',
  VIEW_INSIGHTS: 'viewInsights',
} as const;

export type ActionButtonKeys =
  (typeof ActionButtonKeys)[keyof typeof ActionButtonKeys];

export const renderAuthenticateButton = (
  model: Model,
  option: ActionButtonOption,
  handleUpdate: UpdateHandler<Model, Msg>
) => html`
  <fly-tooltip
    content=${DestinationLocale.en.errors.authenticationExpiredForExport}
  >
    <fly-list-item
      preventSelection
      @click=${(e: CustomEvent) => {
        e.preventDefault();
        e.stopPropagation();
        option.handleAction?.(model, option, handleUpdate);
      }}
    >
      ${lang.actions.authenticate}
    </fly-list-item>
  </fly-tooltip>
`;

export const renderEnableButton = (
  model: Model,
  option: ActionButtonOption,
  handleUpdate: UpdateHandler<Model, Msg>
) => html`
  <fly-list-item
    preventSelection
    @click=${() => option?.handleAction?.(model, option, handleUpdate)}
  >
    ${lang.actions.enable}
  </fly-list-item>
`;

export const renderDisableButton = (
  model: Model,
  option: ActionButtonOption,
  handleUpdate: UpdateHandler<Model, Msg>
) => html`
  <fly-list-item
    preventSelection
    @click=${() => option?.handleAction?.(model, option, handleUpdate)}
  >
    ${lang.actions.disable}
  </fly-list-item>
`;

export const renderRunButton = (model: Model) => html`
  <audience-export-snapshot
    audience-export-id=${model.audienceExport.id}
    last-snapshot=${model.audienceExport.last_snapshot_attempt}
    ?audience-active=${model.audienceExport.is_enabled &&
    model.audienceExport.snapshotable}
  ></audience-export-snapshot>
`;

export const renderManageButton = (
  model: Model,
  option: ActionButtonOption
) => {
  return html`
    <fly-list-item
      preventSelection
      hasIcon
      ?disabled=${option.getVisibility?.(model).disabled}
      @click=${() => option.handleAction?.(model, option)}
    >
      ${option.label}
      <div slot="icon">
        <fly-icon icon="settings"></fly-icon>
      </div>
    </fly-list-item>
  `;
};

export const renderExtendButton = (
  model: Model,
  option: ActionButtonOption
) => {
  return html`
    <fly-list-item
      preventSelection
      hasIcon
      ?disabled=${option.getVisibility?.(model).disabled}
      @click=${() => option.handleAction?.(model, option)}
    >
      ${option.label}
      <div slot="icon">
        <fly-icon icon="schedule"></fly-icon>
      </div>
    </fly-list-item>
  `;
};

export const renderIngestButton = (
  model: Model,
  option: ActionButtonOption,
  handleUpdate: UpdateHandler<Model, Msg>
) => {
  const content = html`
    <fly-list-item
      preventSelection
      ?disabled=${option.getVisibility?.(model).disabled}
      hasIcon
      @click=${() => option.handleAction?.(model, option, handleUpdate)}
    >
      ${option.label}
      <div slot="icon">
        <fly-icon icon="sync_alt"></fly-icon>
      </div>
    </fly-list-item>
  `;

  if (option.getVisibility?.(model).disabled) {
    return html`
      <fly-tooltip content=${lang.tooltips.ingest.disabled} placement="top">
        ${content}
      </fly-tooltip>
    `;
  }

  return content;
};

export const renderViewInsightsButton = (
  model: Model,
  option: ActionButtonOption
) => {
  const content = html`
    <fly-list-item
      preventSelection
      ?disabled=${option.getVisibility?.(model).disabled}
      hasIcon
      @click=${() => option.handleAction?.(model, option)}
    >
      ${option.label}
      <div slot="icon">
        <fly-icon icon="leaderboard"></fly-icon>
      </div>
    </fly-list-item>
  `;

  if (option.getVisibility?.(model).disabled) {
    return html`
      <fly-tooltip content=${lang.tooltips.insights.disabled} placement="top">
        ${content}
      </fly-tooltip>
    `;
  }

  return content;
};
